import React from "react";

import { Editor, EditorProps } from "@monaco-editor/react";
import { getFormErrorMessage } from "components/pages/measurement-devices/utils/utils";
import { Chips, ChipsProps } from "primereact/chips";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import { InputNumber, InputNumberProps } from "primereact/inputnumber";
import { InputText, InputTextProps } from "primereact/inputtext";
import { MultiSelect, MultiSelectProps } from "primereact/multiselect";
import { ToggleButton, ToggleButtonProps } from "primereact/togglebutton";
import { ControllerRenderProps, FieldErrors, FieldValues } from "react-hook-form";
import CalendarWrapper, { CalendarWrapperProps } from "../calendar-wrapper/CalendarWrapper";
import ColorPicker, { ColorPickerProps } from "../color-picker/ColorPicker";
import InputLabelWrapper from "../input-label-wrapper/InputLabelWrapper";

type ControllerInputProps<T extends FieldValues> = {
  errors: FieldErrors<T>;
  field: ControllerRenderProps<T, any>;
  label: string | React.ReactNode;
  col?: number;
};

const labelString = (label: string | React.ReactNode) => (typeof label === "string" ? label : undefined);

export const ControllerTextInput = <T extends FieldValues>({
  field,
  errors,
  label,
  col,
  ...inputProps
}: ControllerInputProps<T> & InputTextProps) => {
  return (
    <InputLabelWrapper labelName={label} col={col ?? 6}>
      <InputText
        value={field.value ?? ""}
        onChange={e => {
          field.onChange(e.target.value);
        }}
        className="p-inputtext-sm"
        name={field.name}
        placeholder={inputProps.placeholder ?? labelString(label)}
        {...inputProps}
      />
      {getFormErrorMessage(field.name, errors)}
    </InputLabelWrapper>
  );
};

export const ControllerNumberInput = <T extends FieldValues>({
  field,
  errors,
  label,
  col,
  ...inputProps
}: ControllerInputProps<T> & InputNumberProps) => {
  return (
    <InputLabelWrapper labelName={label} col={col ?? 6}>
      <InputNumber
        value={field.value}
        onChange={e => {
          field.onChange(e.value);
        }}
        className="p-inputtext-sm"
        name={field.name}
        placeholder={inputProps.placeholder ?? labelString(label)}
        useGrouping={false}
        maxFractionDigits={2}
        {...inputProps}
      />
      {getFormErrorMessage(field.name, errors)}
    </InputLabelWrapper>
  );
};

export const ControllerDropdownInput = <T extends FieldValues>({
  field,
  errors,
  label,
  col,
  ...inputProps
}: ControllerInputProps<T> & DropdownProps) => {
  return (
    <InputLabelWrapper labelName={label} col={col}>
      <Dropdown
        value={field.value}
        onChange={e => field.onChange(e.value)}
        className="p-inputtext-sm"
        name={field.name}
        placeholder={inputProps.placeholder ?? labelString(label)}
        {...inputProps}
      />
      {getFormErrorMessage(field.name, errors)}
    </InputLabelWrapper>
  );
};

export const ControllerToggleInput = <T extends FieldValues>({
  field,
  errors,
  label,
  col,
  ...inputProps
}: ControllerInputProps<T> & ToggleButtonProps) => {
  return (
    <InputLabelWrapper labelName={label} col={col}>
      <ToggleButton
        checked={field.value}
        value={field.value}
        onChange={e => {
          field.onChange(e.value);
        }}
        className="p-inputtext-sm"
        name={field.name}
        {...inputProps}
      />
    </InputLabelWrapper>
  );
};

export const ControllerMultiSelect = <T extends FieldValues>({
  field,
  errors,
  label,
  col,
  ...inputProps
}: ControllerInputProps<T> & MultiSelectProps) => {
  return (
    <InputLabelWrapper labelName={label} col={col}>
      <MultiSelect
        value={field.value}
        onChange={e => field.onChange(e.value)}
        className="p-inputtext-sm"
        name={field.name}
        placeholder={inputProps.placeholder ?? labelString(label)}
        {...inputProps}
      />
      {getFormErrorMessage(field.name, errors)}
    </InputLabelWrapper>
  );
};

export const ControllerCalendar = <T extends FieldValues>({
  field,
  errors,
  label,
  col,
  ...inputProps
}: ControllerInputProps<T> & Partial<CalendarWrapperProps>) => {
  return (
    <InputLabelWrapper labelName={label} col={col}>
      <CalendarWrapper
        showTime
        name={field.name}
        placeholder={inputProps.placeholder ?? labelString(label)}
        {...inputProps}
        value={field.value ? new Date(field.value) : undefined}
        onChange={val => field.onChange(val)}
      />
      {getFormErrorMessage(field.name, errors)}
    </InputLabelWrapper>
  );
};

export const ControllerChips = <T extends FieldValues>({
  field,
  errors,
  label,
  col,
  ...inputProps
}: ControllerInputProps<T> & ChipsProps) => {
  return (
    <InputLabelWrapper labelName={label} col={col}>
      <div className="p-fluid">
        <Chips
          value={field.value}
          onChange={val => field.onChange(val)}
          placeholder={inputProps.placeholder ?? labelString(label)}
          {...inputProps}
        />
      </div>
      {getFormErrorMessage(field.name, errors)}
    </InputLabelWrapper>
  );
};

export const ControllerEditor = <T extends FieldValues>({
  field,
  errors,
  label,
  col,
  ...inputProps
}: ControllerInputProps<T> & EditorProps) => {
  return (
    <InputLabelWrapper labelName={label} col={col}>
      <Editor
        height="36rem"
        value={field.value ?? ""}
        onChange={e => field.onChange(e)}
        defaultLanguage="html"
        className="border-1 border-300 border-round overflow-hidden"
        options={{
          lineNumbers: "off",
          lineDecorationsWidth: 0,
          minimap: {
            enabled: false,
          },
        }}
        {...inputProps}
      />
      {getFormErrorMessage(field.name, errors)}
    </InputLabelWrapper>
  );
};

export const ControllerColorPicker = <T extends FieldValues>({
  field,
  errors,
  label,
  col,
  ...inputProps
}: ControllerInputProps<T> & Partial<ColorPickerProps>) => {
  return (
    <InputLabelWrapper labelName={label} col={col}>
      <ColorPicker value={field.value ?? ""} onValueChange={e => field.onChange(e)} {...inputProps} />
      {getFormErrorMessage(field.name, errors)}
    </InputLabelWrapper>
  );
};
